import { NODE_ROW_HEIGHT, TASK_ROW_HEIGHT } from "../constants";
import { LaneLayout } from "./laneLayout";

/**
 * The rows both panes of the planner draw, built once from the data and from what the user has expanded.
 *
 * The tree is resource > project > scope > task group > task. A collapsed row stands for everything beneath it and
 * stacks the bars of all those allocations into lanes; an expanded row shows no bars of its own because they move
 * to its children. Every row has the height of its kind whatever it holds: a collapsed row draws only its first
 * lanes, and the tasks of a row in a period are counted and listed on request (see `tasksInPeriod`).
 * Because the left panel (names, hours) and the timeline (bars) are both drawn from these rows, with the same
 * heights and offsets, they cannot get out of step.
 */

export const RowType = { RESOURCE: "resource", PROJECT: "project", SCOPE: "scope", GROUP: "taskGroup", TASK: "task" };

const EMPTY_SET = new Set();

/** Keys are paths, so everything beneath a node starts with the key of the node followed by "/". */
export const resourceKey = (resourceId) => `r:${resourceId}`;
export const projectKey = (parentKey, projectId) => `${parentKey}/p:${projectId}`;
export const scopeKey = (parentKey, scopeId) => `${parentKey}/s:${scopeId}`;
export const groupKey = (parentKey, groupId) => `${parentKey}/g:${groupId}`;
export const taskRowKey = (parentKey, taskId) => `${parentKey}/t:${taskId}`;

const byName = (a, b) => (a.name || "").localeCompare(b.name || "", undefined, { numeric: true, sensitivity: "base" });

/**
 * Which nodes are expanded, per resource: `{ [resourceId]: Set<key> }`. Collapsing a node also collapses everything
 * beneath it, so expanding it again starts from its children closed.
 */
export function toggleExpanded(expanded, resourceId, key) {
  const current = expanded[resourceId] || EMPTY_SET;
  const next = new Set(current);

  if (current.has(key)) {
    for (const candidate of current) {
      if (candidate === key || candidate.startsWith(`${key}/`)) next.delete(candidate);
    }
  } else {
    next.add(key);
  }

  const result = { ...expanded };
  if (next.size === 0) delete result[resourceId];
  else result[resourceId] = next;
  return result;
}

export function createRowModelCache() {
  return { layouts: new Map(), blocks: new Map() };
}

/** The lanes of a collapsed node, laid out again only as far as its allocations changed. */
function layoutFor(cache, key, version, allocations) {
  const items = allocations.map((allocation) => ({ key: allocation.taskId, startDay: allocation.startDay, endDay: allocation.endDay }));
  let entry = cache.layouts.get(key);

  if (!entry) {
    entry = { version: -1, layout: new LaneLayout() };
    cache.layouts.set(key, entry);
  }
  if (entry.version !== version) {
    entry.layout.sync(items);
    entry.version = version;
  }
  return entry.layout;
}

// Grouping a resource's allocations is needed by every node of the resource, so it is done once per version. The
// list of dated allocations is replaced whenever any allocation changes and is kept when only the covered tiles do,
// which makes it the key.
const indexes = new WeakMap();

function indexAllocations(allocs) {
  let index = indexes.get(allocs.dated);
  if (index) return index;

  const datedByProject = new Map();
  const datedByScope = new Map();
  const datedByGroup = new Map();
  const push = (map, key, allocation) => {
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(allocation);
  };

  for (const allocation of allocs.dated) {
    push(datedByProject, allocation.projectId, allocation);
    push(datedByScope, allocation.scopeId, allocation);
    if (allocation.groupId) push(datedByGroup, allocation.groupId, allocation);
  }

  // Tasks of a scope or group in the order their rows are listed: by start, tasks without dates last.
  const ungroupedByScope = new Map();
  const tasksByGroup = new Map();
  for (const allocation of [...allocs.dated, ...allocs.undated]) {
    if (allocation.groupId) push(tasksByGroup, allocation.groupId, allocation);
    else push(ungroupedByScope, allocation.scopeId, allocation);
  }

  index = { datedByProject, datedByScope, datedByGroup, ungroupedByScope, tasksByGroup };
  indexes.set(allocs.dated, index);
  return index;
}

const NO_ALLOCATIONS = [];

function nodeRow({ key, type, depth, resourceId, projectId = null, scopeId = null, groupId = null, expandable, expanded, allocations, byTask, cache, version }) {
  if (expanded) {
    return { key, type, depth, resourceId, projectId, scopeId, groupId, expandable, expanded, layout: null, byTask: null, allocations, height: NODE_ROW_HEIGHT };
  }

  const layout = layoutFor(cache, key, version, allocations);
  return { key, type, depth, resourceId, projectId, scopeId, groupId, expandable, expanded, layout, byTask, allocations, height: NODE_ROW_HEIGHT };
}

function taskRow(parentKey, depth, resourceId, projectId, scopeId, groupId, allocation) {
  return {
    key: taskRowKey(parentKey, allocation.taskId),
    type: RowType.TASK,
    depth,
    resourceId,
    projectId,
    scopeId,
    groupId,
    taskId: allocation.taskId,
    allocation,
    expandable: false,
    expanded: false,
    layout: null,
    byTask: null,
    height: TASK_ROW_HEIGHT,
  };
}

function buildResourceRows(data, resource, allocs, expandedSet, cache) {
  const rows = [];
  const index = indexAllocations(allocs);
  const version = allocs.version;
  const resourceRowKey = resourceKey(resource.id);
  const resourceExpanded = expandedSet.has(resourceRowKey);

  rows.push(
    nodeRow({
      key: resourceRowKey,
      type: RowType.RESOURCE,
      depth: 0,
      resourceId: resource.id,
      expandable: resource.memberships.length > 0,
      expanded: resourceExpanded,
      allocations: allocs.dated,
      byTask: allocs.byTask,
      cache,
      version,
    })
  );
  if (!resourceExpanded) return rows;

  for (const membership of resource.memberships) {
    const projectRowKey = projectKey(resourceRowKey, membership.projectId);
    const projectExpanded = expandedSet.has(projectRowKey);
    const scopes = membership.scopeIds.map((id) => data.scopes[id]).filter(Boolean).sort(byName);

    rows.push(
      nodeRow({
        key: projectRowKey,
        type: RowType.PROJECT,
        depth: 1,
        resourceId: resource.id,
        projectId: membership.projectId,
        expandable: scopes.length > 0,
        expanded: projectExpanded,
        allocations: index.datedByProject.get(membership.projectId) || NO_ALLOCATIONS,
        byTask: allocs.byTask,
        cache,
        version,
      })
    );
    if (!projectExpanded) continue;

    for (const scope of scopes) {
      const scopeRowKey = scopeKey(projectRowKey, scope.id);
      const scopeExpanded = expandedSet.has(scopeRowKey);
      const groups = (data.groupsByScope[scope.id] || []).map((id) => data.groups[id]).filter(Boolean).sort(byName);
      const ungrouped = index.ungroupedByScope.get(scope.id) || NO_ALLOCATIONS;
      const hasTasks = ungrouped.length > 0 || groups.length > 0;

      rows.push(
        nodeRow({
          key: scopeRowKey,
          type: RowType.SCOPE,
          depth: 2,
          resourceId: resource.id,
          projectId: membership.projectId,
          scopeId: scope.id,
          expandable: hasTasks,
          expanded: scopeExpanded,
          allocations: index.datedByScope.get(scope.id) || NO_ALLOCATIONS,
          byTask: allocs.byTask,
          cache,
          version,
        })
      );
      if (!scopeExpanded) continue;

      for (const group of groups) {
        const groupRowKey = groupKey(scopeRowKey, group.id);
        const groupExpanded = expandedSet.has(groupRowKey);
        const groupTasks = index.tasksByGroup.get(group.id) || NO_ALLOCATIONS;

        rows.push(
          nodeRow({
            key: groupRowKey,
            type: RowType.GROUP,
            depth: 3,
            resourceId: resource.id,
            projectId: membership.projectId,
            scopeId: scope.id,
            groupId: group.id,
            expandable: groupTasks.length > 0,
            expanded: groupExpanded,
            allocations: index.datedByGroup.get(group.id) || NO_ALLOCATIONS,
            byTask: allocs.byTask,
            cache,
            version,
          })
        );
        if (!groupExpanded) continue;

        for (const allocation of groupTasks) {
          rows.push(taskRow(groupRowKey, 4, resource.id, membership.projectId, scope.id, group.id, allocation));
        }
      }

      for (const allocation of ungrouped) {
        rows.push(taskRow(scopeRowKey, 3, resource.id, membership.projectId, scope.id, null, allocation));
      }
    }
  }

  return rows;
}

/**
 * Builds the rows for the resources in the order they were paged. Rows of a resource whose allocations, structure
 * and expanded nodes did not change are reused as they were, so a change to one resource builds the rows of that
 * resource only.
 *
 * @returns { rows, tops, totalHeight, spans } where `tops[i]` is the offset of `rows[i]` and `spans[resourceId]` is
 *   `{ first, count }`, the rows that belong to one resource.
 */
export function buildRowModel(data, expanded, cache) {
  const rows = [];
  const spans = {};
  const liveResources = new Set();

  for (const resourceId of data.order) {
    const resource = data.resources[resourceId];
    const allocs = data.allocs[resourceId];
    if (!resource || !allocs) continue;

    liveResources.add(resourceId);
    const expandedSet = expanded[resourceId] || EMPTY_SET;

    // The rows depend on the allocations (their version), the structure and what is expanded, not on which tiles
    // are covered or being fetched, so those changes reuse the rows as they are.
    let block = cache.blocks.get(resourceId);
    if (!block || block.version !== allocs.version || block.structureVersion !== data.structureVersion || block.expandedSet !== expandedSet) {
      block = { version: allocs.version, structureVersion: data.structureVersion, expandedSet, rows: buildResourceRows(data, resource, allocs, expandedSet, cache) };
      cache.blocks.set(resourceId, block);
    }

    spans[resourceId] = { first: rows.length, count: block.rows.length };
    for (const row of block.rows) rows.push(row);
  }

  for (const resourceId of cache.blocks.keys()) {
    if (!liveResources.has(resourceId)) cache.blocks.delete(resourceId);
  }

  const tops = new Array(rows.length);
  let offset = 0;
  for (let i = 0; i < rows.length; i += 1) {
    tops[i] = offset;
    offset += rows[i].height;
  }

  return { rows, tops, totalHeight: offset, spans };
}

/** Index of the first row that ends below `offset`, or the row count when there is none. */
function firstRowBelow(model, offset) {
  let low = 0;
  let high = model.rows.length;
  while (low < high) {
    const middle = (low + high) >>> 1;
    if (model.tops[middle] + model.rows[middle].height <= offset) low = middle + 1;
    else high = middle;
  }
  return low;
}

/** The rows that overlap the vertical range, as `{ first, last }` indices (last is first - 1 when there are none). */
export function rowsInRange(model, top, bottom) {
  const first = firstRowBelow(model, top);
  let last = first - 1;
  while (last + 1 < model.rows.length && model.tops[last + 1] < bottom) last += 1;
  return { first, last };
}

/**
 * The row at a vertical position and how far below that position the row starts (zero or less when it starts above).
 * Remembering this is what lets the list stay put when rows above it change height.
 */
export function anchorAt(model, offset) {
  const index = firstRowBelow(model, offset);
  if (index >= model.rows.length) return null;

  return { key: model.rows[index].key, distance: model.tops[index] - offset };
}

/** The vertical position at which an anchored row is as far from the top as it was, or null when the row is gone. */
export function offsetKeeping(model, anchor) {
  const index = model.rows.findIndex((row) => row.key === anchor.key);
  return index === -1 ? null : model.tops[index] - anchor.distance;
}

/**
 * The allocations a resource, project, scope or task group row stands for, collapsed or not, that overlap the closed
 * range of days, in the order of their start. Allocations without dates are never in a period.
 */
export function tasksInPeriod(row, startDay, endDay) {
  if (!row.allocations) return [];

  return row.allocations
    .filter((allocation) => allocation.startDay <= endDay && allocation.endDay >= startDay)
    .sort((a, b) => a.startDay - b.startDay || (a.name || "").localeCompare(b.name || ""));
}

/** The ids of the resources whose rows are within a range of row indices, in list order. */
export function resourcesInRows(model, first, last) {
  const ids = [];
  for (let i = Math.max(0, first); i <= last && i < model.rows.length; i += 1) {
    const id = model.rows[i].resourceId;
    if (ids[ids.length - 1] !== id) ids.push(id);
  }
  return ids;
}
